import { AdminCohorts } from '@platform/client';
import { useQueryClient } from '@tanstack/react-query';
import { useContext, useState } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';
import { AdminFormModal, ModalStage } from '../../AdminFormModal';
import { AdminTableContext } from '../../AdminTable';
import { CohortForm, CohortFormData } from './CohortForm';

export const EditCohortModal = (props: { cohortId: number; onClose: () => void }) => {
  const [modalStage, setModalStage] = useState<ModalStage>('form');
  const { data: cohortData, isSuccess } = AdminCohorts.useGetCohortDetails(props.cohortId);
  const { adminQueryOptionsV2 } = useContext(AdminTableContext);
  const { ...functions } = useForm<CohortFormData>({
    values: {
      name: cohortData?.name || null,
      clientId: cohortData?.clientId ? parseInt(cohortData.clientId.toString()) : null,
      description: cohortData?.description ?? null,
      available: cohortData?.available || false,
      url: cohortData?.url || null,
      engagementStartDate: cohortData?.engagementStartDate || null,
      engagementEndDate: cohortData?.engagementEndDate || null,
      wrapupDate: cohortData?.wrapupDate || null,
      kickoffDate: cohortData?.kickoffDate || null,
      applicationStartDate: cohortData?.applicationStartDate || null,
      applicationEndDate: cohortData?.applicationEndDate || null,
      duration: cohortData?.duration || null,
      templateId: cohortData?.template?.id.toString() || null,
      timeCommitment: cohortData?.timeCommitment || null,
    },
  });
  const queryClient = useQueryClient();
  const updateCohortMutator = AdminCohorts.useUpdateCohortDetails({
    mutation: {
      onMutate: () => setModalStage('submitting'),
      onError: () => setModalStage('error'),
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: [...AdminCohorts.getGetAdminCohortsQueryKey({ ...adminQueryOptionsV2 })],
        });
        queryClient.invalidateQueries({
          queryKey: [...AdminCohorts.getGetCohortDetailsQueryKey(props.cohortId)],
        });
        setModalStage('success');
      },
    },
  });
  const onSubmit: SubmitHandler<CohortFormData> = (data) => {
    if (
      data.name &&
      data.clientId &&
      data.engagementStartDate &&
      data.engagementEndDate &&
      data.wrapupDate &&
      data.kickoffDate &&
      data.applicationStartDate &&
      data.applicationEndDate &&
      data.duration &&
      data.timeCommitment &&
      data.templateId
    ) {
      updateCohortMutator.mutate({
        cohortId: props.cohortId,
        data: {
          name: data.name,
          clientId: data.clientId,
          available: data.available,
          description: data.description ?? null,
          url: data.url ?? null,
          engagementStartDate: data.engagementStartDate,
          engagementEndDate: data.engagementEndDate,
          wrapupDate: data.wrapupDate,
          kickoffDate: data.kickoffDate,
          applicationStartDate: data.applicationStartDate,
          applicationEndDate: data.applicationEndDate,
          duration: data.duration,
          timeCommitment: data.timeCommitment,
          templateId: data.templateId,
        },
      });
    }
  };
  return (
    <AdminFormModal
      variant='full-screen'
      title='Edit Cohort'
      modalStage={modalStage}
      onClose={props.onClose}
      submitCTA={{ text: 'Update Cohort', callback: () => functions.handleSubmit(onSubmit)() }}
    >
      {isSuccess && <CohortForm {...functions} />}
    </AdminFormModal>
  );
};
